if (!customElements.get('c-video')) {
  customElements.define(
    'c-video',
    class ComponentVideo extends HTMLElement {
      constructor() {
        super();

        this._activeTimeout = null;
        this.videoPlayUnsubscribe = () => {};
        this.videoStopUnsubscribe = () => {};
      }

      connectedCallback() {
        this.videoContainer = this;
        this.video = this.querySelector('video');
        this.videoButton = this.querySelector('.js-video__video-button');
        this.enableControls = this.dataset.enableControls;
        this.videoLink = this.querySelector('.js-video-link');
        this.videoSource = this.videoLink?.querySelector('source[data-src]');

        if (this.videoLink) {
          this.loadOnInteraction = this.videoLink.hasAttribute('data-load-on-interaction');

          if (this.loadOnInteraction) {
            ['click', 'scroll', 'touchstart', 'mousemove'].forEach((eventName) => {
              document.addEventListener(eventName, this.loadVideo, {
                once: true,
                passive: true,
                capture: true,
              });
            });
          } else {
            this.initVideoViewportObserver();
          }
        }

        if (this.enableControls === 'true') {
          this.videoContainer.addEventListener('click', this.videoClickHandler);
        }

        if (typeof subscribe === 'function') {
          this.videoPlayUnsubscribe = subscribe('component-video:play', this.playVideo);
          this.videoStopUnsubscribe = subscribe('component-video:stop', this.stopVideo);
        }
      }

      disconnectedCallback() {
        if (this.enableControls === 'true') {
          this.videoContainer.removeEventListener('click', this.videoClickHandler);
        }

        this.videoPlayUnsubscribe();
        this.videoStopUnsubscribe();
        this.clearActiveTimer();
      }

      initVideoViewportObserver = () => {
        if (!this.videoLink || typeof IntersectionObserver !== 'function') return;

        const observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.loadVideo();
                obs.disconnect();
              }
            });
          },
          {
            root: null,
            threshold: 0.1,
          }
        );

        observer.observe(this);
      };

      loadVideo = () => {
        if (!this.videoLink) return;

        if (this.videoSource?.dataset.src) {
          this.videoSource.src = this.videoSource.dataset.src;
          this.videoSource.removeAttribute('data-src');
        } else if (this.videoLink.dataset.src) {
          this.videoLink.src = this.videoLink.dataset.src;
          this.videoLink.removeAttribute('data-src');
        } else {
          return;
        }

        this.videoLink.load();
      };

      isMobile = () => {
        return window.matchMedia('(max-width: 768px)').matches;
      };

      clearActiveTimer = () => {
        if (!this._activeTimeout) return;

        clearTimeout(this._activeTimeout);
        this._activeTimeout = null;
      };

      setActiveState = () => {
        if (!this.videoButton) return;

        this.videoButton.classList.add('is-active');
        this.clearActiveTimer();

        this._activeTimeout = setTimeout(() => {
          this.videoButton?.classList.remove('is-active');
          this._activeTimeout = null;
        }, 2000);
      };

      toggleVideo = () => {
        if (!this.video) return;

        if (this.video.paused) {
          this.playVideo();
        } else {
          this.stopVideo();
        }
      };

      videoClickHandler = (event) => {
        if (!this.video) return;

        const hasCustomButton = !!this.videoButton;

        if (hasCustomButton) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          this.clearActiveTimer();

          if (this.video.paused) {
            this.videoButton.classList.remove('is-active');
            this.playVideo();
          } else {
            this.videoButton.classList.add('is-active');
            this.stopVideo();
          }

          return;
        }

        if (this.isMobile()) {
          const isActive = this.videoButton?.classList.contains('is-active');

          if (!isActive) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.setActiveState();
            return;
          }

          this.clearActiveTimer();
          this.videoButton?.classList.remove('is-active');
          this.toggleVideo();
          return;
        }

        this.videoButton?.classList.remove('is-active');
        this.toggleVideo();
      };

      playVideo = () => {
        if (!this.video) return;

        if (typeof publish === 'function') {
          publish('hero-banner-pause');
        }

        this.video.play();
        this.videoButton?.setAttribute('data-pause', 'false');
        this.videoButton?.setAttribute('data-show', 'true');
      };

      stopVideo = () => {
        if (!this.video) return;

        this.video.pause();
        this.videoButton?.setAttribute('data-pause', 'true');
        this.videoButton?.setAttribute('data-show', 'true');
      };
    }
  );
}
